import { create } from 'zustand';
import { useEffect } from 'react';
import { useSettings } from '@/state/settings';
import { resetSave, ascend } from '@/engine/save';
import { useMeta } from '@/state/meta';
import { tierByRank } from '@/lib/tier';
import { THEME_META } from '@/theme/themes';
import { ACHIEVEMENTS } from '@/engine/achievements';
import { BUFF_DEFS, type BuffId } from '@/state/meta';
import { cn } from '@/lib/cn';

interface SettingsPanelStore {
  open: boolean;
  toggle: () => void;
  close: () => void;
}

export const useSettingsPanel = create<SettingsPanelStore>((set, get) => ({
  open: false,
  toggle: () => set({ open: !get().open }),
  close: () => set({ open: false }),
}));

export function SettingsPanel() {
  const open = useSettingsPanel((s) => s.open);
  const close = useSettingsPanel((s) => s.close);
  const muted = useSettings((s) => s.muted);
  const toggleMute = useSettings((s) => s.toggleMute);
  const reducedMotion = useSettings((s) => s.reducedMotion);
  const setReducedMotion = useSettings((s) => s.setReducedMotion);
  const themeId = useSettings((s) => s.themeId);
  const setTheme = useSettings((s) => s.setTheme);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  // ALL hooks must be called BEFORE any early return (Rules of Hooks)
  const carryCoins = useMeta((s) => s.carryCoins);
  const ascensions = useMeta((s) => s.ascensions);
  const highest = useMeta((s) => s.highestTierRankReached);
  const highestTier = tierByRank(highest);
  const loreCodex = useMeta((s) => s.loreCodex);
  const loreEntries = Object.values(loreCodex).sort((a, b) => b.firstSeenMs - a.firstSeenMs);
  const unlockedAchievements = useMeta((s) => s.unlockedAchievements);
  const loginStreak = useMeta((s) => s.loginStreak);

  if (!open) return null;

  const handleReset = () => {
    if (!confirm('Wipe save and reload? This cannot be undone.')) return;
    resetSave();
    location.reload();
  };

  const handleAscend = () => {
    if (!confirm('Ascend now? Your character retires; you carry over Carry Coins + tier flag. This cannot be undone.')) return;
    ascend();
    close();
  };

  return (
    <>
      <button
        onClick={close}
        aria-label="Close settings"
        className="fixed inset-0 z-40 bg-black/40"
        tabIndex={-1}
      />
      <aside className="fixed right-3 top-14 z-50 w-[min(92vw,360px)] overflow-y-auto rounded border border-[var(--border-strong)] bg-[var(--bg-deep)] p-4 shadow-2xl">
        <div className="mb-3 flex items-center justify-between border-b border-[var(--border-subtle)] pb-2">
          <h2 className="text-mono-md font-bold text-[var(--text-primary)]">Settings</h2>
          <button onClick={close} className="text-mono-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            close [esc]
          </button>
        </div>

        <Section title="Identity">
          <HandleInput />
        </Section>

        <Section title="Audio">
          <Toggle label="Sound" value={!muted} onChange={toggleMute} />
        </Section>

        <Section title="Visual">
          <Toggle label="Reduced motion" value={reducedMotion} onChange={(v) => setReducedMotion(v)} />
          <div className="mt-1 text-mono-xs text-[var(--text-muted)]">
            <span className="block mb-1">Theme</span>
            <div className="grid grid-cols-2 gap-1">
              {THEME_META.map((t) => {
                const meta = useMeta.getState();
                const isLocked =
                  (t.unlockCondition?.highestTierRank ?? 0) > meta.highestTierRankReached ||
                  (t.unlockCondition?.ascensions ?? 0) > meta.ascensions;
                return (
                  <button
                    key={t.id}
                    onClick={() => !isLocked && setTheme(t.id)}
                    disabled={isLocked}
                    className={cn(
                      'rounded border px-2 py-1 text-left text-mono-xs',
                      themeId === t.id
                        ? 'border-[var(--accent-primary)] bg-[var(--bg-elevated)] text-[var(--accent-primary)]'
                        : 'border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]',
                      isLocked && 'opacity-30 cursor-not-allowed',
                    )}
                  >
                    {t.label}
                    {isLocked && t.unlockCondition?.highestTierRank && (
                      <span className="block text-[10px] text-[var(--text-muted)]">unlock: tier {t.unlockCondition.highestTierRank}</span>
                    )}
                    {isLocked && t.unlockCondition?.ascensions && (
                      <span className="block text-[10px] text-[var(--text-muted)]">unlock: {t.unlockCondition.ascensions}× ascend</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </Section>

        <Section title="Meta-progression">
          <div className="flex items-center justify-between text-mono-xs py-1">
            <span className="text-[var(--text-secondary)]">Carry Coins</span>
            <span className="tabular-nums text-[var(--accent-primary)]">{carryCoins}</span>
          </div>
          <div className="flex items-center justify-between text-mono-xs py-1">
            <span className="text-[var(--text-secondary)]">Ascensions</span>
            <span className="tabular-nums text-[var(--text-primary)]">{ascensions}</span>
          </div>
          <div className="flex items-center justify-between text-mono-xs py-1">
            <span className="text-[var(--text-secondary)]">Highest tier</span>
            <span className="text-[var(--text-primary)]">{highestTier?.label ?? '—'}</span>
          </div>
          <div className="flex items-center justify-between text-mono-xs py-1">
            <span className="text-[var(--text-secondary)]">Login streak</span>
            <span className="text-[var(--accent-primary)]">{loginStreak} {loginStreak === 1 ? 'day' : 'days'} 🔥</span>
          </div>
          <button
            onClick={handleAscend}
            className="mt-2 w-full rounded border border-[var(--accent-primary)] px-3 py-2 text-mono-xs text-[var(--accent-primary)] hover:bg-[var(--accent-primary)] hover:text-black"
          >
            ascend (retire this character, keep buffs)
          </button>
        </Section>

        <Section title="Carry Coin Shop">
          {(Object.keys(BUFF_DEFS) as BuffId[]).map((id) => {
            const def = BUFF_DEFS[id];
            const buffsState = useMeta.getState().buffs;
            const owned =
              (id === 'skip-boot' && buffsState.skipBoot) ||
              (id === 'starting-coin-bonus-1' && buffsState.passiveCoinBonus);
            const canAfford = carryCoins >= def.cost;
            return (
              <div key={id} className="flex items-center justify-between gap-2 py-1 text-mono-xs">
                <span className="text-[var(--text-secondary)] truncate">{def.label}</span>
                <button
                  onClick={() => useMeta.getState().buyBuff(id)}
                  disabled={!canAfford || !!owned}
                  className={cn(
                    'rounded border px-2 py-0.5 text-[10px] uppercase whitespace-nowrap',
                    owned
                      ? 'border-[var(--positive)] text-[var(--positive)]'
                      : canAfford
                      ? 'border-[var(--accent-primary)] text-[var(--accent-primary)] hover:bg-[var(--accent-primary)] hover:text-black'
                      : 'border-[var(--border-subtle)] text-[var(--text-muted)] cursor-not-allowed',
                  )}
                >
                  {owned ? 'owned' : `${def.cost} 🪙`}
                </button>
              </div>
            );
          })}
        </Section>

        <Section title={`Lore Codex`}>
          {loreEntries.length === 0 ? (
            <p className="text-mono-xs italic text-[var(--text-muted)]">no storylines discovered yet. give it time. ignore your wife.</p>
          ) : (
            <div className="flex flex-col gap-1">
              {loreEntries.map((e) => (
                <div key={e.id} className="rounded border border-[var(--border-subtle)] bg-[var(--bg-deep)] px-2 py-1 text-mono-xs">
                  <span className="text-[var(--text-primary)]">{e.title}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title={`Achievements`}>
          <div className="flex flex-col gap-1">
            {ACHIEVEMENTS.map((a) => {
              const unlocked = unlockedAchievements.includes(a.id);
              return (
                <div
                  key={a.id}
                  className={cn(
                    'rounded border px-2 py-1 text-mono-xs',
                    unlocked
                      ? 'border-[var(--accent-primary)] bg-[var(--bg-deep)]'
                      : 'border-[var(--border-subtle)] bg-[var(--bg-deep)] opacity-30',
                  )}
                >
                  <div className={cn('font-bold', unlocked ? 'text-[var(--accent-primary)]' : 'text-[var(--text-muted)]')}>
                    {unlocked ? '🏆 ' : '🔒 '}{unlocked ? a.label : '???'}
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)]">
                    {unlocked ? a.description : 'locked'}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        <Section title="Save">
          <button
            onClick={handleReset}
            className="w-full rounded border border-[var(--negative)] px-3 py-2 text-mono-xs text-[var(--negative)] hover:bg-[var(--negative)] hover:text-black"
          >
            wipe save & start over (no carry-over)
          </button>
        </Section>

        <p className="mt-3 text-[10px] italic text-[var(--text-muted)]">
          theme unlocks tied to milestones land in Phase 5. for now, only bloomberg-amber is wired.
        </p>
      </aside>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4">
      <div className="mb-1 text-mono-xs font-bold uppercase tracking-wider text-[var(--accent-primary)]">{title}</div>
      <div className="rounded border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-2">
        {children}
      </div>
    </section>
  );
}

function HandleInput() {
  const handle = useSettings((s) => s.playerHandle);
  const setHandle = useSettings((s) => s.setPlayerHandle);
  return (
    <label className="flex items-center justify-between gap-2 py-1 text-mono-xs">
      <span className="text-[var(--text-secondary)]">Your handle</span>
      <span className="flex items-center gap-1 rounded border border-[var(--border-subtle)] bg-[var(--bg-deep)] px-2 py-0.5">
        <span className="text-[var(--text-muted)]">@</span>
        <input
          type="text"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          maxLength={20}
          className="w-32 bg-transparent text-mono-xs text-[var(--text-primary)] outline-none"
          aria-label="Your Y handle"
        />
      </span>
    </label>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-2 py-1 text-mono-xs">
      <span className="text-[var(--text-secondary)]">{label}</span>
      <button
        onClick={() => onChange(!value)}
        role="switch"
        aria-checked={value}
        className={cn(
          'relative h-5 w-10 rounded-full transition-colors',
          value ? 'bg-[var(--accent-primary)]' : 'bg-[var(--bg-elevated)]',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-4 w-4 rounded-full bg-black transition-transform',
            value ? 'translate-x-5' : 'translate-x-0.5',
          )}
        />
      </button>
    </label>
  );
}
